import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { apiAxios } from '@/api/index'
import { fetchWrapper } from '@/reducers/fetch'
import type { ReduxState } from '@/utils/typeHelpers'

export interface SafeHarborCatchUpQuarter {
  /** `YYYY-Q`. */
  taxQuarter: string
  quarterNumber: number
  /** ISO string over the wire; the API sends a Date. */
  dueDate: string | null
  perQuarterInCents: number
  paidInCents: number
}

export interface SafeHarborCatchUp {
  perQuarterInCents: number
  quartersCovered: number
  paymentsLoggedInCents: number
  totalDueNowInCents: number
  /** One entry per covered quarter, ascending. */
  quarters: SafeHarborCatchUpQuarter[]
}

export interface SafeHarborCatchUpPayload {
  taxYear: string
  taxQuarter: string
  /** Null until the member completes the calculator for that quarter. */
  catchUp: SafeHarborCatchUp | null
}

const safeHarborCatchUpSlice = createSlice({
  name: 'safeHarborCatchUp',
  initialState: null as SafeHarborCatchUpPayload | null,
  reducers: {
    setSafeHarborCatchUp: (
      _state,
      action: PayloadAction<SafeHarborCatchUpPayload>
    ) => action.payload,
  },
})

export default safeHarborCatchUpSlice.reducer

const { setSafeHarborCatchUp } = safeHarborCatchUpSlice.actions

export const FETCH_SAFE_HARBOR_CATCH_UP_KEY = 'FETCH_SAFE_HARBOR_CATCH_UP_KEY'

export const fetchSafeHarborCatchUp = () =>
  fetchWrapper({
    fetchKey: FETCH_SAFE_HARBOR_CATCH_UP_KEY,
    defaultErrorMessage: 'There was an error fetching your safe harbor total.',
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.get<SafeHarborCatchUpPayload>(
        '/finances/api/v1/tax_estimates/safe_harbor_catch_up'
      )

      dispatch(setSafeHarborCatchUp(json.data))
      return json.data
    },
  })

/**
 * The whole payload, not just the figures: the card resolves its own quarter
 * from the estimate row and needs to check the route resolved the same one.
 */
export const selectSafeHarborCatchUp = (state: ReduxState) =>
  state.safeHarborCatchUp
